import { DATA_SOURCES_TEST_PREFIX, GetAdditionalTableFields, toggleValueInList } from "../Utils";
import { DataSourceSetupComposite, enumATSModes } from "./DataSourceSetupComposite";
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Flex,
  Input,
  isNullish,
  TooltipWrapper
} from "@theorgchartdev/storybook";
import * as React from "react";
import cx from "classnames";
import { appstr } from "@coreapp/utils/appstr";
import { appcstr } from "@coreapp/utils/appcstr";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";
import { InputValuePills } from "@/appComponents/Blocks/Composites/InputValuePills/InputValuePills";

export const DayforceSetupComposite = ({
  config,
  onChange,
  resetIntegration,
  index,
  importType,
  chartConfig
}) => {
  const [activeStatusInput, setActiveStatusInput] = React.useState("");
  const [inactiveStatusInput, setInactiveStatusInput] = React.useState("");

  const additionalFields = GetAdditionalTableFields(config.source);

  let {
    dftables,
    dfInactiveXRefCodes,
    dfActiveXRefCodes,
    dfendpointtoggle,
    dfendpoint,
    dfIDPrefix
  } = config;
  let selectedTables = dftables ?? [];

  const isAdditionalSource = importType !== "import";

  let additionalFieldOptions = additionalFields.map(table => {
    return { value: table, label: table };
  });

  let selectedFieldOptions = selectedTables.map(table => {
    return { value: table, label: table };
  });

  const showResetInfo = isAdditionalSource
    ? !isNullish(config["headers"])
    : !isNullish(chartConfig["headers"]) && !isNullish(chartConfig["Timestamp"]);
  // For this connector, there is no Additional Fields dropdown in the DataSourceSetupComposite component,
  // but instead there is an "Additional Tables" dropdown that works the same way. It is semantically
  // different.

  const onTableChange = e => {
    let option = e.value;
    const selectedList = toggleValueInList(selectedTables, option.value);
    handleUpdate({ dftables: selectedList });
  };

  const handleUpdate = newData => {
    onChange({ ...config, ...newData });
  };

  const { message, alertProps } = createBoldToastMessage("Dayforce reset info", {
    resetLabel: appstr("Reset integration")
  });

  const handleEnterKeyPress = (e, statusType) => {
    if (e.key !== "Enter") return;

    const activeStatus = statusType === "active";
    e.preventDefault();
    const currentValue = activeStatus ? activeStatusInput : inactiveStatusInput;
    if (currentValue === "") return;

    let currentStatuses = activeStatus ? dfActiveXRefCodes : dfInactiveXRefCodes;

    if (currentStatuses === "" || isNullish(currentStatuses)) currentStatuses = currentValue;
    else currentStatuses += "," + currentValue;

    const updateObj = activeStatus
      ? { dfActiveXRefCodes: currentStatuses }
      : { dfInactiveXRefCodes: currentStatuses };

    handleUpdate(updateObj);
    activeStatus ? setActiveStatusInput("") : setInactiveStatusInput("");
  };

  const handleStatusInput = (event, statusType) => {
    const value = event.target.value;
    if (statusType === "active") {
      setActiveStatusInput(value);
    } else if (statusType === "inactive") {
      setInactiveStatusInput(value);
    }
  };

  const onChangePill = (newValues, statusType) => {
    if (isNullish(newValues)) return;

    const activeStatus = statusType === "active";

    const newStatuses = [...newValues];
    const updateObj = activeStatus
      ? { dfActiveXRefCodes: newStatuses.join(",") }
      : { dfInactiveXRefCodes: newStatuses.join(",") };

    handleUpdate(updateObj);
  };

  const onRemovePill = (value, statusType) => {
    if (isNullish(value)) return;
    const activeStatus = statusType === "active";
    const currentStatuses = activeStatus
      ? dfActiveXRefCodes.split(",")
      : dfInactiveXRefCodes.split(",");
    const newStatuses = currentStatuses.filter(item => item.trim() !== value.trim());
    const updateObj = activeStatus
      ? { dfActiveXRefCodes: newStatuses.join(",") }
      : { dfInactiveXRefCodes: newStatuses.join(",") };
    handleUpdate(updateObj);
  };

  return (
    <DataSourceSetupComposite
      config={config}
      onChange={handleUpdate}
      index={index}
      chartConfig={chartConfig}
      showInputControls
      showAdditionalFields
      onAdditionalFieldChange={onTableChange}
      additionalFieldOptions={additionalFieldOptions}
      selectedAdditionalFields={selectedFieldOptions}
      importType={importType}
      showConfiguration={isAdditionalSource}
      showLoadHeaders={isAdditionalSource}
      ATSType={isAdditionalSource ? enumATSModes.notATS : undefined}
    >
      <Flex direction={"column"}>
        <Input
          id={"df-inactive-statuses"}
          dataTestId={`${DATA_SOURCES_TEST_PREFIX}-dayforce-inactive-statuses-input`}
          label={appcstr("Inactive Statuses")}
          value={inactiveStatusInput}
          autocomplete={"off"}
          type={"text"}
          size={"medium"}
          placeholder={appstr("Type and press enter")}
          onChange={event => handleStatusInput(event, "inactive")}
          onKeyDown={e => handleEnterKeyPress(e, "inactive")}
          expandable={true}
          prefixNode={
            dfInactiveXRefCodes ? (
              <InputValuePills
                key={"df-inactive-status"}
                values={dfInactiveXRefCodes.split(",")}
                inputProps={{ inputSize: "medium" }}
                onChange={value => onChangePill(value, "inactive")}
                onRemoveItem={value => onRemovePill(value, "inactive")}
              />
            ) : null
          }
          clearable={dfInactiveXRefCodes !== "" && !isNullish(dfInactiveXRefCodes)}
          onResetInput={() => handleUpdate({ dfInactiveXRefCodes: "" })}
        />
        <Input
          id={"df-active-statuses"}
          dataTestId={`${DATA_SOURCES_TEST_PREFIX}-dayforce-active-statuses-input`}
          label={appcstr("dfstatus1")}
          value={activeStatusInput}
          autocomplete={"off"}
          type={"text"}
          size={"medium"}
          placeholder={appstr("Type and press enter")}
          onChange={event => handleStatusInput(event, "active")}
          onKeyDown={e => handleEnterKeyPress(e, "active")}
          expandable={true}
          prefixNode={
            dfActiveXRefCodes ? (
              <InputValuePills
                key={"df-active-status"}
                values={dfActiveXRefCodes.split(",")}
                inputProps={{ inputSize: "medium" }}
                onChange={value => onChangePill(value, "active")}
                onRemoveItem={value => onRemovePill(value, "active")}
              />
            ) : null
          }
          clearable={dfActiveXRefCodes !== "" && !isNullish(dfActiveXRefCodes)}
          onResetInput={() => handleUpdate({ dfActiveXRefCodes: "" })}
        />
      </Flex>
      <Checkbox
        id={"set-endpoint"}
        data-testid={`${DATA_SOURCES_TEST_PREFIX}-dayforce-specify-endpoint-checkbox`}
        size={"medium"}
        label={appstr("Specify endpoint")}
        checked={dfendpointtoggle}
        onChange={() => {
          const toggle = !dfendpointtoggle;
          handleUpdate({ dfendpointtoggle: toggle, dfendpoint: "" });
        }}
      />
      {dfendpointtoggle && (
        <Input
          id={"set-endpoint-input"}
          dataTestId={`${DATA_SOURCES_TEST_PREFIX}-dayforce-endpoint-input`}
          inputOnly
          size={"medium"}
          placeholder={"www.dayforcehcm.com"}
          disabled={!dfendpointtoggle}
          value={dfendpoint}
          onChange={event => {
            handleUpdate({ dfendpoint: event.target.value });
          }}
        />
      )}
      {isAdditionalSource && (
        <Input
          id={"df-id-prefix"}
          dataTestId={"df-id-prefix"}
          label={appcstr("dfidprefix")}
          helperText={appcstr("dfidprefixhelper")}
          size={"medium"}
          type={"text"}
          autocomplete={"off"}
          placeholder={appstr("Optional")}
          value={dfIDPrefix ?? ""}
          onChange={event => handleUpdate({ dfIDPrefix: event.target.value })}
          clearable={!isNullish(dfIDPrefix) && dfIDPrefix !== ""}
          onResetInput={() => handleUpdate({ dfIDPrefix: "" })}
        />
      )}
      {showResetInfo && (
        <TooltipWrapper tooltipProps={{ message: appstr("Reset data integration") }}>
          <Flex gap={"12px"} direction={"column"}>
            <Divider />
            <Alert
              className={cx("oc-padding-small", "oc-text-small", "oc-data-source-details")}
              {...alertProps}
            >
              {message}
            </Alert>
            <Button
              dataTestId={"df-hard-refresh"}
              onClick={() => resetIntegration()}
              iconProps={{ name: "arrows-rotate" }}
              modifier={"danger"}
              size={"medium"}
            >
              {appstr("Reset integration")}
            </Button>
          </Flex>
        </TooltipWrapper>
      )}
    </DataSourceSetupComposite>
  );
};
